import { Throbber } from '@edgebox/react-components';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { FormatDate } from '../../FormatDate';
import { BP_FIGURE_TITLE_CLASSES, BP_TAG_CHIP_CLASSES, BP_TEXT_BUTTON_SMALL_CLASSES } from '../Governance/Components/brandPresenceClasses';
import { HEALTH_TINT_CLASSES } from '../Governance/Components/HealthScoreCell';
import { resolveHealthCheckScorePresentation } from '../Governance/Onboarding/ContentInventory/Components/HealthCheckScorePresentation';
import { ParamsComponent } from '../ParamsComponent';
import {
  COLLAPSED_TAGS_SHOWN,
  IResolvedPageFigures,
  hasAnyFigure,
  PageFigureTag,
  PageFiguresParams,
  pageFiguresFromRecord,
  resolvePageFigures,
  sameResolvedFigures,
} from './pageFigures';

/** What a screen reader is told, once, when the figures on the screen change to the ones Sync Core holds. */
export const LIVE_FIGURES_ANNOUNCEMENT = 'These figures now come from Content Sync.';

/** What a screen reader is told when the answer takes the figures the site put on the screen off it. */
export const EMPTY_FIGURES_ANNOUNCEMENT = 'Content Sync holds no figures for this page.';

/** Where a live read of the page's figures stands. */
interface ILiveFigures {
  /** Whether the request is still out, which is the whole of what the box says while it is. */
  loading: boolean;
  /** The figures Sync Core answered with, once it has. */
  figures?: PageFiguresParams;
}

/**
 * Read the page's figures from Sync Core once, in the background, after the site's own are on screen.
 *
 * The request carries the frame's own content token and the page the site named, so what comes back
 * is that page's record in the calling site's project. The box asks once per load: the site's figures
 * are there from the first paint, and this only ever replaces them with fresher ones.
 *
 * Under that one ask the read is a client read like every other the app makes, so the client's own
 * retry budget governs the attempts. A refusal — no record for this page, a token the service will
 * not take — carries a status and ends the read at once; a dropped connection or a service in
 * trouble carries none and is retried on that budget, which is how long the reading indicator stays
 * up before the box falls silent.
 *
 * A frame the host page handed no token makes no request at all. The token is read from the
 * configuration the provider was given rather than inferred from the client existing: the provider
 * wraps the whole app and builds its client whatever that configuration carries, so a box waiting for
 * a client would ask with an empty token in the header, be refused, and swallow the refusal — one
 * request per load of every page of the site, for an answer that cannot arrive.
 *
 * Every way it can fail — no record for this page, a token the service refuses, a network the frame
 * cannot reach — ends the same way: the site's own figures stay on the screen and the box says
 * nothing about it. A visitor of the site reads a page, not a status console, and the site owner sees
 * the figures they passed either way.
 */
function useLivePageFigures(page: PageFiguresParams): ILiveFigures {
  const context = useContext(SyncCoreApiContext);
  const api = context?.api;
  const token = context?.jwt;
  const [live, setLive] = useState<ILiveFigures>({ loading: false });
  const asked = useRef(false);
  const onScreen = useRef(false);

  useEffect(() => {
    onScreen.current = true;
    return () => {
      onScreen.current = false;
    };
  }, []);

  useEffect(() => {
    if (asked.current || !api || !token) {
      return;
    }
    asked.current = true;
    setLive({ loading: true });

    const read = async () => {
      try {
        const record = await api.governance.contentItems.pageFigures({
          entityType: page.entityType,
          entityUuid: page.entityUuid,
          langcode: page.langcode,
        });
        if (onScreen.current) {
          setLive({ loading: false, figures: pageFiguresFromRecord({ identity: page, properties: record?.properties || [] }) });
        }
      } catch {
        if (onScreen.current) {
          setLive({ loading: false });
        }
      }
    };

    void read();
  }, [api, token, page]);

  return live;
}

/**
 * One figure of the page, as one line of the list: what it is called on the left, its value on the
 * right.
 *
 * The box is served to one host only, a site's content-edit screen, which puts it in the narrow column
 * beside the editor — a region a few hundred pixels wide. The cards the screens inside the app show the
 * same numbers in are built for a row across a whole page, and in that column they stand two to a row,
 * wrap their titles and push everything else below the fold. A line per figure keeps the four figures
 * in a few lines at any width the column takes, and neither half of a line ever wraps: the label is a
 * few words, and the value a number or one word.
 *
 * The list is the definition list the app's compact figures are written as elsewhere (the tooltip of
 * the content health trends chart), so a figure's name and its value are read out as a pair.
 */
function FigureRow(props: { label: string; value: React.ReactNode; note?: string }) {
  return (
    <>
      <dt className="py-1 text-sm font-normal text-slate-500 whitespace-nowrap truncate">
        {props.label}
        {props.note ? <span className="text-xs text-slate-500"> {props.note}</span> : null}
      </dt>
      <dd className="m-0 py-1 text-right text-sm font-semibold tabular-nums text-slate-900 whitespace-nowrap">{props.value}</dd>
    </>
  );
}

/**
 * The page's health on the tint of its severity band, the way the app's health tables show a figure:
 * the band is read from the same presentation the check badges and meters use, so a red figure here
 * and a red meter there mean the same thing.
 */
function HealthFigure(props: { percent: number }) {
  const { label } = resolveHealthCheckScorePresentation(props.percent);

  return <span className={`rounded-md px-1.5 py-0.5 ${HEALTH_TINT_CLASSES[label]}`}>{`${props.percent}%`}</span>;
}

/**
 * The page's tags as small chips, the first few of them at once and the rest behind one control.
 *
 * The control says how many more there are, and opening it shows them up to the number the box shows
 * at all; what that bound leaves out is counted under the chips, in the words it always was. Opening
 * and closing it grows and shrinks the document, which the host page follows, so the frame is as tall
 * as what is open and never scrolls on its own.
 *
 * A chip's name wraps inside the chip rather than running past the column: a tag's name is the term's
 * own and can be long, and a chip wider than the frame would put a scrollbar inside it.
 */
function PageTags(props: { tags: PageFigureTag[]; hiddenTagCount: number }) {
  const { tags, hiddenTagCount } = props;
  const [expanded, setExpanded] = useState(false);
  const listId = React.useId();
  const shown = expanded ? tags : tags.slice(0, COLLAPSED_TAGS_SHOWN);
  // The control counts what opening it shows; what the bound leaves out even then is said once it is open.
  const foldedCount = tags.length - COLLAPSED_TAGS_SHOWN;

  return (
    <div className="mt-3 border-t border-slate-200/70 pt-3">
      <p className={`mb-2 ${BP_FIGURE_TITLE_CLASSES}`}>Tags</p>
      <ul id={listId} className="m-0 flex list-none flex-wrap gap-1.5 p-0">
        {shown.map((tag) => (
          <li key={tag.key}>
            <span className={BP_TAG_CHIP_CLASSES}>{tag.name}</span>
          </li>
        ))}
      </ul>
      {expanded && hiddenTagCount > 0 ? <p className="text-xs text-slate-500 mt-1.5 mb-0">and {hiddenTagCount} more</p> : null}
      {tags.length > COLLAPSED_TAGS_SHOWN ? (
        <button
          type="button"
          className={`mt-1.5 ${BP_TEXT_BUTTON_SMALL_CLASSES}`}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show fewer' : `+${foldedCount} more`}
        </button>
      ) : null}
    </div>
  );
}

/**
 * What a screen reader is told about the answer, in the words of what the answer did to the box.
 *
 * Silence is the answer to everything a person looking at the box would not notice: no answer yet, an
 * answer carrying the figures already on the screen, and an answer with nothing this box shows for a
 * box that was showing nothing either.
 *
 * The two sentences are kept apart because they describe opposite screens. Figures arriving is the
 * first; figures going, which takes a full box down to the one sentence saying the page has none, is
 * the second — the largest change the box can make to itself, and the one a person who cannot see it
 * has the most to be told about.
 */
function announcementFor(own: IResolvedPageFigures, answered?: IResolvedPageFigures): string {
  if (answered === undefined || sameResolvedFigures(own, answered)) {
    return '';
  }

  if (hasAnyFigure(answered)) {
    return LIVE_FIGURES_ANNOUNCEMENT;
  }

  return hasAnyFigure(own) ? EMPTY_FIGURES_ANNOUNCEMENT : '';
}

/**
 * One page's figures: the ones the site that embeds this frame passed, and then the ones Sync Core
 * holds for the same page as soon as it has answered.
 *
 * Every region is omitted when its figure is absent, so a site holding half of them shows half a box
 * rather than a row of dashes. A count of zero is a figure and is shown as `0`.
 *
 * Which page these are is carried on the wrapper as data attributes rather than written into the
 * copy: a machine name and an id are keys, and the people who read this box are the site's own
 * visitors. A site owner telling one box from another reads them there, and in the debug rows the
 * surrounding component renders.
 */
function PageFigures(props: { figures: PageFiguresParams }) {
  const { figures } = props;
  const live = useLivePageFigures(figures);
  // One resolution, whichever side the figures came from, so the live ones can never render by
  // different rules than the site's own.
  //
  // The answer replaces what the site passed whole rather than filling gaps in it: the record Sync Core
  // answers with is the page's figures as they stand, and what the site passed is an older delivery of
  // that same record. A figure the answer does not carry is therefore one the page no longer has rather
  // than one lost on the way — a page Sync Core has never health-scored is sent no percentage, a page it
  // holds no priority for is sent none, and an unclassified page is sent an empty term list, so each of
  // those regions leaves the screen. Merging the site's older copy back in would keep a figure on the
  // screen that nothing holds any more.
  const own: IResolvedPageFigures = resolvePageFigures(figures);
  const answered: IResolvedPageFigures | undefined = live.figures ? resolvePageFigures(live.figures) : undefined;
  const resolved: IResolvedPageFigures = answered ?? own;

  // Spoken only when a person looking at the box would see something different from what was there a
  // moment ago, and said in the words of what they would see — see {@link announcementFor}.
  const announcement = announcementFor(own, answered);
  const rows = [
    resolved.contentHealthPercent !== undefined ? (
      <FigureRow key="content-health" label="Content health" value={<HealthFigure percent={resolved.contentHealthPercent} />} />
    ) : null,
    resolved.importance !== undefined ? <FigureRow key="importance" label="Page priority" value={resolved.importance} /> : null,
    resolved.openIssueCount !== undefined ? <FigureRow key="issues" label="Open issues" value={`${resolved.openIssueCount}`} /> : null,
    resolved.citedInAnswersLast30Days !== undefined ? (
      <FigureRow key="cited" label="Cited in answers" note="(last 30 days)" value={`${resolved.citedInAnswersLast30Days}x`} />
    ) : null,
  ].filter((row) => row !== null);

  const identity = {
    'data-testid': 'page-figures-box',
    'data-entity-type': figures.entityType,
    'data-entity-uuid': figures.entityUuid,
    'data-langcode': figures.langcode,
  };

  // The indicator is laid over the box rather than given a row of its own, so it takes no space at any
  // point: the host page sizes the frame to this document, and a row that appeared when the read
  // started and went when it ended would grow and shrink the frame, jolting whatever the site renders
  // below it twice per load. Out of the flow, the frame reports the same height before, during and
  // after the read, and nothing inside the box moves either.
  //
  // It sits in the box's bottom right corner, in a band the copy's own padding keeps clear of it — clear
  // at every width, because the frame is as wide as the region the site put it in, so an indicator drawn
  // over the text's box covers the last line of the copy on a narrow page and nothing at all on a wide
  // one. The band belongs to the box whether a read is running or not, so reserving it adds nothing to
  // the frame when the indicator appears and takes nothing away when it goes.
  //
  // The indicator is drawn for the eye and for nothing else. The throbber the component library draws
  // is a live region in its own right, so leaving it in the accessibility tree would say "Loading" on
  // every page of the site on every load and then take the region away without a word when a read
  // fails — and this box has one region to speak from, a few lines down. The throbber takes a label of
  // its own but keeps its role whatever it is given, so the wrapper is what hides it: `aria-hidden` and
  // a presentational role take the whole indicator out of the tree.
  //
  // The announcement beside it is spoken rather than drawn: the figures change one number for another,
  // which says nothing out loud.
  //
  // It carries a key and sits at the same place under the same parent whichever figures the box is
  // showing, because the transition it exists for — "This page has no figures" giving way to a full
  // list — is the one that would otherwise tear the region down and mount a fresh one with the
  // sentence already inside it. A live region that arrives with its text in place is not a change to a
  // live region, and assistive technology says nothing about it.
  const liveRead = (
    <React.Fragment key="live-read">
      {live.loading ? (
        <div data-testid="page-figures-indicator" className="absolute bottom-0 right-0 p-3" aria-hidden="true" role="presentation">
          <Throbber />
        </div>
      ) : null}
      <span data-testid="page-figures-announcement" className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </React.Fragment>
  );

  return (
    <div className="relative px-2 pt-1 pb-5 text-sm text-slate-700" {...identity}>
      {hasAnyFigure(resolved) ? (
        <>
          {rows.length ? <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3">{rows}</dl> : null}

          {resolved.tags.length ? <PageTags tags={resolved.tags} hiddenTagCount={resolved.hiddenTagCount} /> : null}

          {resolved.summaryUpdated !== undefined ? (
            <p className="text-xs text-slate-500 mt-3 mb-0">
              Updated <FormatDate withTime={true}>{moment.unix(resolved.summaryUpdated)}</FormatDate>
            </p>
          ) : null}
        </>
      ) : (
        <p className="mb-0 text-sm text-slate-500">This page has no figures. They arrive with the next update from Content Sync.</p>
      )}

      {liveRead}
    </div>
  );
}

export const PageFiguresBox = () => (
  <ParamsComponent<PageFiguresParams> Params={PageFiguresParams}>
    {(params: PageFiguresParams) => <PageFigures figures={params} />}
  </ParamsComponent>
);
